/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

/**
 *  This hook allows to stop wheel events from propagating to parent elements.
 *  Normally, when you scroll a child element, the scroll event will propagate to the parent element,
 *  as calling event.stopPropagation() on syncthetic events does not stop the propagation
 */

import React from 'react';

interface ScrollInIsolationProps {
  onWheel?: (event: WheelEvent) => void;
}

export const useScrollingInIsolation = <T extends HTMLElement>(
  options?: ScrollInIsolationProps,
): React.RefObject<T> => {
  const scrollInIsolationRef = React.useRef(null);

  React.useEffect(() => {
    const element = scrollInIsolationRef.current as HTMLElement | null;
    if (!element) {
      return;
    }

    const handleWheel = (event: WheelEvent): void => {
      if (event.metaKey || event.ctrlKey) {
        event.stopPropagation();
        event.preventDefault();
      }
      if (options?.onWheel) {
        options.onWheel(event);
      }
    };
    element.addEventListener('wheel', handleWheel, { passive: false });

    // eslint-disable-next-line consistent-return
    return (): void => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [options]);

  return scrollInIsolationRef;
};
